import { FC, useEffect, useMemo, useState } from "react";
import CardComponent from "../../../../../../common/components/card/card.component";
import TableComponent from "../../../../../../common/components/table/table.component";
import dateService from "../../../../../../common/utils/date/date.service";
import RelatedRailyTaxiBillingTableRow from "./types/related-raily-taxi-billing-table-row";
import relatedRailyTaxiBillingsDataFactory from "./factory/related-raily-taxi-billings-data.factory";
import RelatedRailyTaxiBillingData from "./types/related-raily-taxi-billing data";
import relatedOrdersBillingsHelper from "./related-raily-taxi-billings.helper";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import billingsTranslationsHelper from "../../../../../../languages/billings-translations.helper";
import TableLinkButtonComponent from "../../../../../../common/components/table/button/link/table-link-button.component";
import billingRoutesHelper from "../../../../common/routes/billing-routes.helper";
import useBillingsOrderRelatedRailyTaxi from "../../../../../../common/services/billings/order/related/raily-taxi/use-billings-order-related-raily-taxi";
import useAbort from "../../../../../../common/hooks/use-abort";
import BillingsOrderRelatedRailyTaxiParams from "../../../../../../common/services/billings/order/related/raily-taxi/billings-order-related-raily-taxi-params";
import BillingsOrderRelatedRailyTaxi from "../../../../../../common/services/billings/order/related/raily-taxi/billings-order-related-raily-taxi";

type RelatedRailyTaxiBillingsProps = {
  orderId: string | undefined;
};

const RelatedRailyTaxiBillingsComponent: FC<RelatedRailyTaxiBillingsProps> = (
  props,
) => {
  const [billingsData, setBillingsData] =
    useState<RelatedRailyTaxiBillingData[]>();

  const billingsOrderRelatedRailyTaxi = useBillingsOrderRelatedRailyTaxi();
  const billingsOrderRelatedRailyTaxiAbort = useAbort();

  useEffect(() => {
    if (props.orderId) {
      const params: BillingsOrderRelatedRailyTaxiParams = {
        orderUuid: props.orderId,
      };

      billingsOrderRelatedRailyTaxi.load(
        params,
        billingsOrderRelatedRailyTaxiAbort.signal,
      );

      billingsOrderRelatedRailyTaxiAbort.revoke;
    }
  }, [props.orderId]);

  useEffect(() => {
    if (!billingsOrderRelatedRailyTaxi.data) {
      return;
    }
    onBillingDataFetchSuccess(billingsOrderRelatedRailyTaxi.data);
  }, [billingsOrderRelatedRailyTaxi.data]);

  const onBillingDataFetchSuccess = (
    response: BillingsOrderRelatedRailyTaxi[],
  ) => {
    const billingsData =
      relatedRailyTaxiBillingsDataFactory.createBillingsData(response);

    setBillingsData(billingsData);
  };

  const onTableRowClick = () => {};

  const createTableRow = (
    billingData: RelatedRailyTaxiBillingData,
  ): RelatedRailyTaxiBillingTableRow => {
    return {
      id: billingData.internalOrderId.toString(),
      onClick: onTableRowClick,
      value: {
        date: (
          <div title={dateService.format(billingData.date, "dd/mm/yyyy")}>
            {dateService.format(billingData.date, "dd/mm/yyyy")}
          </div>
        ),
        internalOrderId: (
          <div title={billingData.internalOrderId.toString()}>
            {billingData.internalOrderId}
          </div>
        ),
        companyName: (
          <div title={billingData.contractDetails?.companyName}>
            {billingData.contractDetails?.companyName}
          </div>
        ),
        model: (
          <div title={billingData.contractDetails?.model}>
            {billingData.contractDetails?.model}
          </div>
        ),
        rate: (
          <div title={billingData.contractDetails?.distanceRate.toString()}>
            {billingData.contractDetails?.distanceRate.toFixed(2)} PLN
          </div>
        ),
        distance: (
          <div title={billingData.distance.toString()}>
            {billingData.distance}
          </div>
        ),
        routeDistanceCost: (
          <div title={billingData.routeDistanceCost.toString()}>
            {billingData.routeDistanceCost.toFixed(2)} PLN
          </div>
        ),
        bonus: (
          <div title={billingData.bonus.toString()}>
            {billingData.bonus.toFixed(2)} PLN
          </div>
        ),
        penalty: (
          <div title={billingData.penalty.toString()}>
            {billingData.penalty.toFixed(2)} PLN
          </div>
        ),
        haltingCost: (
          <div title={billingData.haltingCost.toString()}>
            {billingData.haltingCost.toFixed(2)} PLN
          </div>
        ),
        highwayCost: (
          <div title={billingData.highwayCharge.toString()}>
            {billingData.highwayCharge.toFixed(2)} PLN
          </div>
        ),
        total: (
          <div title={billingData?.total.toString()}>
            {billingData.total.toFixed(2)} PLN
          </div>
        ),
        actions: (
          <TableLinkButtonComponent
            openInNewTab
            icon={faEdit}
            to={billingRoutesHelper.getRailyWithTaxiAddRoute({
              orderUuid: billingData.uuid,
            })}
            type="primary"
            idForTesting={"raily-with-taxi-related-billing-button"}
            classNames={{
              root: "related_order_billings_table_edit_billing_icon",
            }}
          />
        ),
      },
    };
  };

  const tableColumns = relatedOrdersBillingsHelper.getTableColumns();

  const tableRows: RelatedRailyTaxiBillingTableRow[] = useMemo(() => {
    if (billingsData) {
      return billingsData.map((listingItem) => createTableRow(listingItem));
    }

    return [];
  }, [billingsData]);

  const translations =
    billingsTranslationsHelper.getCargoRailyAddBillingsTranslations()
      .relatedBillings.raily_taxi;

  return (
    <CardComponent
      classNames={{ root: "related_order_billings" }}
      header={{ title: translations.summaryLabel }}
    >
      <TableComponent
        isLoading={billingsOrderRelatedRailyTaxi.isLoading}
        columns={tableColumns}
        rows={tableRows}
      />
    </CardComponent>
  );
};

export default RelatedRailyTaxiBillingsComponent;
